import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Tabs,
  Tab,
  Button,
  Grid,
  Paper,
  TextField,
  InputAdornment,
  Divider,
  IconButton,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  CircularProgress,
  Snackbar,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Link as LinkIcon,
  ContentCopy as CopyIcon,
  FileCopy as FileCopyIcon,
  Share as ShareIcon,
  OpenInNew as OpenInNewIcon,
  Image as ImageIcon
} from '@mui/icons-material';
import { useAuth } from '../../../contexts/AuthContext';
import canvaIntegrationService, { 
  SavedCanvaAsset, 
  CanvaAssetCategory 
} from '../../../services/canvaIntegrationService';
import CanvaEmbeddedPicker from '../../../components/CanvaEmbeddedPicker';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`asset-tabpanel-${index}`}
      aria-labelledby={`asset-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `asset-tab-${index}`,
    'aria-controls': `asset-tabpanel-${index}`,
  };
}

const AssetGallery: React.FC = () => {
  const params = useParams();
  const organizationId = params.id;
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  // Get route parameters
  const { category, assetId, eventId, tripId, membershipId } = params;
  
  const [tabValue, setTabValue] = useState(0);
  const [organizationAssets, setOrganizationAssets] = useState<SavedCanvaAsset[]>([]);
  const [eventAssets, setEventAssets] = useState<SavedCanvaAsset[]>([]);
  const [tripAssets, setTripAssets] = useState<SavedCanvaAsset[]>([]);
  const [membershipAssets, setMembershipAssets] = useState<SavedCanvaAsset[]>([]);
  
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [canvaPickerOpen, setCanvaPickerOpen] = useState<boolean>(false);
  const [importCategory, setImportCategory] = useState<CanvaAssetCategory>('organization');
  
  const [selectedAsset, setSelectedAsset] = useState<SavedCanvaAsset | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [editDialogOpen, setEditDialogOpen] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  
  const [editTitle, setEditTitle] = useState<string>('');
  const [editDescription, setEditDescription] = useState<string>('');
  const [editCategory, setEditCategory] = useState<CanvaAssetCategory>('organization');
  
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [activeAssetForMenu, setActiveAssetForMenu] = useState<SavedCanvaAsset | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Load assets on component mount
  useEffect(() => {
    if (organizationId) {
      fetchAssets();
      
      // Set the initial tab based on route parameters
      if (category) {
        switch (category) {
          case 'organization':
            setTabValue(0);
            setImportCategory('organization');
            break;
          case 'event':
            setTabValue(1);
            setImportCategory('event');
            break;
          case 'trip':
            setTabValue(2);
            setImportCategory('trip');
            break;
          case 'membership':
            setTabValue(3);
            setImportCategory('membership');
            break;
        }
      } else if (eventId) {
        setTabValue(1);
        setImportCategory('event');
      } else if (tripId) {
        setTabValue(2);
        setImportCategory('trip');
      } else if (membershipId) {
        setTabValue(3);
        setImportCategory('membership');
      }
    }
  }, [organizationId, category, eventId, tripId, membershipId]);
  
  const fetchAssets = async () => {
    if (!organizationId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Fetch assets based on route parameters
      if (eventId) {
        // If viewing assets for a specific event
        const evtAssets = await canvaIntegrationService.fetchCanvaAssets(organizationId, 'event', eventId);
        setEventAssets(evtAssets);
        
        // Still fetch others but focus on event assets
        setOrganizationAssets(await canvaIntegrationService.fetchCanvaAssets(organizationId, 'organization'));
        setTripAssets(await canvaIntegrationService.fetchCanvaAssets(organizationId, 'trip'));
        setMembershipAssets(await canvaIntegrationService.fetchCanvaAssets(organizationId, 'membership'));
      } else if (tripId) {
        // If viewing assets for a specific trip
        const trpAssets = await canvaIntegrationService.fetchCanvaAssets(organizationId, 'trip', tripId);
        setTripAssets(trpAssets);
        
        // Still fetch others but focus on trip assets
        setOrganizationAssets(await canvaIntegrationService.fetchCanvaAssets(organizationId, 'organization'));
        setEventAssets(await canvaIntegrationService.fetchCanvaAssets(organizationId, 'event'));
        setMembershipAssets(await canvaIntegrationService.fetchCanvaAssets(organizationId, 'membership'));
      } else if (membershipId) {
        // If viewing assets for a specific membership
        const memAssets = await canvaIntegrationService.fetchCanvaAssets(organizationId, 'membership', membershipId);
        setMembershipAssets(memAssets);
        
        // Still fetch others but focus on membership assets
        setOrganizationAssets(await canvaIntegrationService.fetchCanvaAssets(organizationId, 'organization'));
        setEventAssets(await canvaIntegrationService.fetchCanvaAssets(organizationId, 'event'));
        setTripAssets(await canvaIntegrationService.fetchCanvaAssets(organizationId, 'trip'));
      } else if (category && assetId) {
        // If viewing a specific asset
        // Fetch all assets and then find the specific one
        const categoryAssets = await canvaIntegrationService.fetchCanvaAssets(organizationId, category as CanvaAssetCategory);
        
        // Find the specific asset
        const asset = categoryAssets.find(a => a.id === assetId);
        if (asset) {
          setSelectedAsset(asset);
          // TODO: Open asset detail view
        }
        
        // Set the category assets
        switch (category) {
          case 'organization':
            setOrganizationAssets(categoryAssets);
            setEventAssets(await canvaIntegrationService.fetchCanvaAssets(organizationId, 'event'));
            setTripAssets(await canvaIntegrationService.fetchCanvaAssets(organizationId, 'trip'));
            setMembershipAssets(await canvaIntegrationService.fetchCanvaAssets(organizationId, 'membership'));
            break;
          case 'event':
            setEventAssets(categoryAssets);
            setOrganizationAssets(await canvaIntegrationService.fetchCanvaAssets(organizationId, 'organization'));
            setTripAssets(await canvaIntegrationService.fetchCanvaAssets(organizationId, 'trip'));
            setMembershipAssets(await canvaIntegrationService.fetchCanvaAssets(organizationId, 'membership'));
            break;
          case 'trip':
            setTripAssets(categoryAssets);
            setOrganizationAssets(await canvaIntegrationService.fetchCanvaAssets(organizationId, 'organization'));
            setEventAssets(await canvaIntegrationService.fetchCanvaAssets(organizationId, 'event'));
            setMembershipAssets(await canvaIntegrationService.fetchCanvaAssets(organizationId, 'membership'));
            break;
          case 'membership':
            setMembershipAssets(categoryAssets);
            setOrganizationAssets(await canvaIntegrationService.fetchCanvaAssets(organizationId, 'organization'));
            setEventAssets(await canvaIntegrationService.fetchCanvaAssets(organizationId, 'event'));
            setTripAssets(await canvaIntegrationService.fetchCanvaAssets(organizationId, 'trip'));
            break;
        }
      } else {
        // Fetch assets for each category
        const orgAssets = await canvaIntegrationService.fetchCanvaAssets(organizationId, 'organization');
        const evtAssets = await canvaIntegrationService.fetchCanvaAssets(organizationId, 'event');
        const trpAssets = await canvaIntegrationService.fetchCanvaAssets(organizationId, 'trip');
        const memAssets = await canvaIntegrationService.fetchCanvaAssets(organizationId, 'membership');
        
        setOrganizationAssets(orgAssets);
        setEventAssets(evtAssets);
        setTripAssets(trpAssets);
        setMembershipAssets(memAssets);
      }
    } catch (err) {
      console.error('Error fetching assets:', err);
      setError('Failed to load assets. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    
    // Set import category based on active tab
    switch (newValue) {
      case 0:
        setImportCategory('organization');
        break;
      case 1:
        setImportCategory('event');
        break;
      case 2:
        setImportCategory('trip');
        break;
      case 3:
        setImportCategory('membership');
        break;
    }
  };
  
  const handleCanvaImport = () => {
    setCanvaPickerOpen(true);
  };
  
  const handleDesignImported = (asset: SavedCanvaAsset) => {
    // Add the new asset to the appropriate category list
    switch (asset.category) {
      case 'organization':
        setOrganizationAssets([asset, ...organizationAssets]);
        break;
      case 'event':
        setEventAssets([asset, ...eventAssets]);
        break;
      case 'trip':
        setTripAssets([asset, ...tripAssets]);
        break;
      case 'membership':
        setMembershipAssets([asset, ...membershipAssets]);
        break;
    }
    
    setSuccessMessage('Design imported successfully!');
  };
  
  const handleAssetMenuOpen = (event: React.MouseEvent<HTMLElement>, asset: SavedCanvaAsset) => {
    setActiveAssetForMenu(asset);
    setMenuAnchorEl(event.currentTarget);
  };
  
  const handleAssetMenuClose = () => {
    setMenuAnchorEl(null);
    setActiveAssetForMenu(null);
  };
  
  const handleEditAsset = () => {
    if (!activeAssetForMenu) return;
    
    setSelectedAsset(activeAssetForMenu);
    setEditTitle(activeAssetForMenu.title);
    setEditDescription(activeAssetForMenu.description || '');
    setEditCategory(activeAssetForMenu.category);
    setEditDialogOpen(true);
    handleAssetMenuClose();
  };
  
  const handleDeleteAsset = () => {
    if (!activeAssetForMenu) return;
    
    setSelectedAsset(activeAssetForMenu);
    setDeleteDialogOpen(true);
    handleAssetMenuClose();
  };
  
  const handleOpenInCanva = () => {
    if (!activeAssetForMenu) return;
    
    window.open(activeAssetForMenu.canvaUrl, '_blank');
    handleAssetMenuClose();
  };
  
  const handleCopyLink = () => {
    if (!activeAssetForMenu) return;
    
    navigator.clipboard.writeText(activeAssetForMenu.imageUrl);
    setSuccessMessage('Link copied to clipboard!');
    handleAssetMenuClose();
  };
  
  const confirmDeleteAsset = async () => {
    if (!selectedAsset || !organizationId) return;
    
    try {
      const success = await canvaIntegrationService.deleteCanvaAsset(
        organizationId,
        selectedAsset.id
      );
      
      if (success) {
        // Remove asset from the appropriate list
        switch (selectedAsset.category) {
          case 'organization':
            setOrganizationAssets(organizationAssets.filter(a => a.id !== selectedAsset.id));
            break;
          case 'event':
            setEventAssets(eventAssets.filter(a => a.id !== selectedAsset.id));
            break;
          case 'trip':
            setTripAssets(tripAssets.filter(a => a.id !== selectedAsset.id));
            break;
          case 'membership':
            setMembershipAssets(membershipAssets.filter(a => a.id !== selectedAsset.id));
            break;
        }
        
        setSuccessMessage('Asset deleted successfully');
      } else {
        setError('Failed to delete asset');
      }
    } catch (err) {
      console.error('Error deleting asset:', err);
      setError('Failed to delete asset');
    } finally {
      setDeleteDialogOpen(false);
      setSelectedAsset(null);
    }
  };
  
  const handleSaveAssetEdit = async () => {
    if (!selectedAsset || !organizationId) return;
    
    try {
      // Create updated asset
      const updatedAsset: SavedCanvaAsset = {
        ...selectedAsset,
        title: editTitle,
        description: editDescription,
        category: editCategory,
        updatedAt: new Date().toISOString()
      };
      
      const success = await canvaIntegrationService.updateCanvaAsset(
        organizationId,
        updatedAsset
      );
      
      if (success) {
        // First, remove from the old category if it changed
        if (selectedAsset.category !== updatedAsset.category) {
          switch (selectedAsset.category) {
            case 'organization':
              setOrganizationAssets(organizationAssets.filter(a => a.id !== selectedAsset.id));
              break;
            case 'event':
              setEventAssets(eventAssets.filter(a => a.id !== selectedAsset.id));
              break;
            case 'trip':
              setTripAssets(tripAssets.filter(a => a.id !== selectedAsset.id));
              break;
            case 'membership':
              setMembershipAssets(membershipAssets.filter(a => a.id !== selectedAsset.id));
              break;
          }
        }
        
        // Then add to the new category list
        switch (updatedAsset.category) {
          case 'organization':
            setOrganizationAssets(prev => 
              prev.map(a => a.id === updatedAsset.id ? updatedAsset : a)
            );
            if (selectedAsset.category !== 'organization') {
              setOrganizationAssets(prev => [updatedAsset, ...prev]);
            }
            break;
          case 'event':
            setEventAssets(prev => 
              prev.map(a => a.id === updatedAsset.id ? updatedAsset : a)
            );
            if (selectedAsset.category !== 'event') {
              setEventAssets(prev => [updatedAsset, ...prev]);
            }
            break;
          case 'trip':
            setTripAssets(prev => 
              prev.map(a => a.id === updatedAsset.id ? updatedAsset : a)
            );
            if (selectedAsset.category !== 'trip') {
              setTripAssets(prev => [updatedAsset, ...prev]);
            }
            break;
          case 'membership':
            setMembershipAssets(prev => 
              prev.map(a => a.id === updatedAsset.id ? updatedAsset : a)
            );
            if (selectedAsset.category !== 'membership') {
              setMembershipAssets(prev => [updatedAsset, ...prev]);
            }
            break;
        }
        
        setSuccessMessage('Asset updated successfully');
      } else {
        setError('Failed to update asset');
      }
    } catch (err) {
      console.error('Error updating asset:', err);
      setError('Failed to update asset');
    } finally {
      setEditDialogOpen(false);
      setSelectedAsset(null);
    }
  };
  
  const handleCategoryChange = (event: SelectChangeEvent) => {
    setEditCategory(event.target.value as CanvaAssetCategory);
  };
  
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };
  
  // Filter assets based on search term
  const filterAssets = (assets: SavedCanvaAsset[]) => {
    if (!searchTerm) return assets;
    
    return assets.filter(asset => 
      asset.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
      (asset.description && asset.description.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  };
  
  // Render asset cards
  const renderAssetCards = (assets: SavedCanvaAsset[]) => {
    const filteredAssets = filterAssets(assets);
    
    if (filteredAssets.length === 0) {
      return (
        <Box textAlign="center" py={4}>
          <ImageIcon sx={{ fontSize: 60, color: 'text.secondary', opacity: 0.5 }} />
          <Typography variant="body1" color="text.secondary" mt={2}>
            No assets found in this category.
          </Typography>
        </Box>
      );
    }
    
    return (
      <Grid container spacing={3}>
        {filteredAssets.map(asset => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={asset.id}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardMedia
                component="img"
                height="160"
                image={asset.thumbnailUrl || asset.imageUrl}
                alt={asset.title}
              />
              <CardContent sx={{ flexGrow: 1 }}>
                <Typography variant="h6" component="div" noWrap>
                  {asset.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" component="div" 
                  sx={{ 
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical'
                  }}
                >
                  {asset.description || 'No description'}
                </Typography>
                <Typography variant="caption" display="block" mt={1}>
                  {new Date(asset.createdAt).toLocaleDateString()}
                </Typography>
              </CardContent>
              <CardActions>
                <Button 
                  size="small" 
                  onClick={() => window.open(asset.imageUrl, '_blank')}
                >
                  View
                </Button>
                <IconButton 
                  size="small"
                  onClick={(event) => handleAssetMenuOpen(event, asset)}
                  sx={{ ml: 'auto' }}
                >
                  <MoreVertIcon />
                </IconButton>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };
  
  if (!organizationId) {

    return (
      <Container>
        <Typography variant="h5" component="h1">
          Organization ID is required (Debug: {JSON.stringify(params)})
        </Typography>
      </Container>
    );
  }
  
  return (
    <Container maxWidth="xl">
      <Box sx={{ mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h4" component="h1">
            Asset Gallery
          </Typography>
          
          <Box>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleCanvaImport}
            >
              Import from Canva
            </Button>
          </Box>
        </Box>
        
        <Typography variant="body1" color="text.secondary">
          Manage your Canva designs and assets for your organization, events, trips, and memberships.
        </Typography>
      </Box>
      
      <Paper sx={{ mb: 4 }}>
        <Box p={2} display="flex" justifyContent="space-between">
          <TextField
            placeholder="Search assets..."
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ width: 300 }}
          />
          
          <Button
            startIcon={<FilterListIcon />}
            variant="outlined"
            size="small"
          >
            Filter
          </Button>
        </Box>
        
        <Divider />
        
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="asset category tabs">
            <Tab label="Organization" {...a11yProps(0)} />
            <Tab label="Events" {...a11yProps(1)} />
            <Tab label="Trips" {...a11yProps(2)} />
            <Tab label="Memberships" {...a11yProps(3)} />
          </Tabs>
        </Box>
        
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TabPanel value={tabValue} index={0}>
              {renderAssetCards(organizationAssets)}
            </TabPanel>
            <TabPanel value={tabValue} index={1}>
              {renderAssetCards(eventAssets)}
            </TabPanel>
            <TabPanel value={tabValue} index={2}>
              {renderAssetCards(tripAssets)}
            </TabPanel>
            <TabPanel value={tabValue} index={3}>
              {renderAssetCards(membershipAssets)}
            </TabPanel>
          </>
        )}
      </Paper>
      
      {/* Canva Design Picker Dialog */}
      <CanvaEmbeddedPicker
        open={canvaPickerOpen}
        onClose={() => setCanvaPickerOpen(false)}
        organizationId={organizationId}
        category={importCategory}
        relatedId={importCategory === 'event' ? eventId : importCategory === 'trip' ? tripId : importCategory === 'membership' ? membershipId : undefined}
        onDesignImported={handleDesignImported}
      />
      
      {/* Asset Context Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleAssetMenuClose}
      >
        <MenuItem onClick={handleEditAsset}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleOpenInCanva}>
          <ListItemIcon>
            <OpenInNewIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Open in Canva</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleCopyLink}>
          <ListItemIcon>
            <CopyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Copy Link</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleDeleteAsset} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Asset</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{selectedAsset?.title}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmDeleteAsset} color="error">Delete</Button>
        </DialogActions>
      </Dialog>
      
      {/* Edit Asset Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Asset</DialogTitle>
        <DialogContent>
          <TextField
            label="Title"
            fullWidth
            margin="normal"
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
          />
          
          <TextField
            label="Description"
            fullWidth
            margin="normal"
            multiline
            rows={3}
            value={editDescription}
            onChange={(e) => setEditDescription(e.target.value)}
          />
          
          <FormControl fullWidth margin="normal">
            <InputLabel>Category</InputLabel>
            <Select
              value={editCategory}
              onChange={handleCategoryChange}
              label="Category"
            >
              <MenuItem value="organization">Organization</MenuItem>
              <MenuItem value="event">Event</MenuItem>
              <MenuItem value="trip">Trip</MenuItem>
              <MenuItem value="membership">Membership</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveAssetEdit} color="primary" variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
      
      {/* Success/Error Messages */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage(null)}
      >
        <Alert severity="success" onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      </Snackbar>
      
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AssetGallery;
